import { Component, EventEmitter, Inject, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router'
import moment from 'moment';
import { AttendeeManagmentHelperService, BaseSettingsGetService, CalendarExportService, EventsApiService, HtmlToBbCodeParserService, ProfileService, WebinarsService } from '../../../common/services';
import { AttendeeStatus, CalendarEvent, EventAttendeeStatus, EventModel, EventType, OfflineActivity, SettingsModel, UserAttenderStatus, WebinarDTO } from '../../../common/models';
import { LearnersEventDialogComponent } from '../../../common/components';
import { FormGroup } from '@angular/forms';
import { finalize, mergeMap, Observable, startWith } from 'rxjs';
import { SETTINGS_SERVICE } from '../../../common/constants';

@Component({
    selector: 'lib-learners-event-card',
    templateUrl: './learners-event-card.component.html',
    styleUrls: ['./learners-event-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LearnersEventCardComponent implements OnInit {
  @Output() public attendanceChange: EventEmitter<void> = new EventEmitter();
  @Input() public event!: EventModel;
  @Input() public set settingsForm(data: FormGroup) {
    if (!data) {
      return;
    }

    data.valueChanges.pipe(
      startWith(data.value)
    ).subscribe(settings => {
      this._settings = data.value;
      this.setSettings(settings);
    })
  }
  public get settings(): SettingsModel | undefined {
    return this._settings;
  }

  public isLoading = false;

  public typesMap = {
    [EventType.CUSTOM]: 'COMMON.event',
    [EventType.WEBINAR]: 'COMMON.webinar',
    [EventType.OFFLINE_ACTIVITY]: 'COMMON.offline_activity',
    [EventType.LEARNING_PATH_DEADLINE]: 'COMMON.deadline',
    [EventType.CHALLENGE_DEADLINE]: 'COMMON.deadline',
    [EventType.IN_PERSON]: 'COMMON.in_person_event'
  };

  public attendeeButtonLabel?: string | null;
  public showAttendingButton = false;
  public attending: boolean | null = false;
  public cardsSettings: any;

  public relatedEntityLink: any;

  public readonly types: typeof EventType = EventType;

  public exportCalendarEvent!: CalendarEvent;

  private _settings?: SettingsModel;

  constructor(
    @Inject(SETTINGS_SERVICE) private readonly settingsService: BaseSettingsGetService,
    private readonly router: Router,
    private readonly attendeeManagmentHelperService: AttendeeManagmentHelperService,
    private readonly dialog: MatDialog,
    private readonly htmlToBbCodeParserService: HtmlToBbCodeParserService,
    private readonly calendarExportService: CalendarExportService,
    private readonly profileService: ProfileService,
    private readonly webinarsService: WebinarsService,
    private readonly eventsApiService: EventsApiService
  ) { }


  ngOnInit(): void {
    this.initCardButtonsAndLinks();
    this.initCalendarExport();

    if (!this.settings) {
      this.settingsService.getSettings().subscribe(settings => {
        this.setSettings(settings);
      })
    }
  }

  public initCardButtonsAndLinks() {
    switch (this.event.type) {
      case EventType.LEARNING_PATH_DEADLINE:
        this.relatedEntityLink = `/learning-path/${this.event.relatedEntity?.id}`
        this.showAttendingButton = false;
        break;
      case EventType.CHALLENGE_DEADLINE:
        this.relatedEntityLink = `/challenge/${this.event.relatedEntity?.id}`
        this.showAttendingButton = false;
        break;
      case EventType.CUSTOM:
        this.showAttendingButton = this.attendeeManagmentHelperService.eventAttendeeEnabled(this.event);
        this.attendeeButtonLabel = this.attendeeManagmentHelperService.eventAttendeeButtonLabel(this.event);
        this.attending = this.event.attendeeStatus === EventAttendeeStatus.ATTENDEE_STATUS_REGISTERED;
        break;
      case EventType.IN_PERSON:
      case EventType.WEBINAR:
        this.relatedEntityLink = `/webinars/${this.event.relatedEntity?.id}`
        this.showAttendingButton = false;
        this.attendeeButtonLabel = null;
        this.attending = null;

        break;

      case EventType.OFFLINE_ACTIVITY:
        const activity = this.event.relatedEntity as OfflineActivity & { quizId: string };
        this.relatedEntityLink = `/offline-activity/${activity?.id}?id=${activity.quizId}`;
        this.showAttendingButton = this.attendeeManagmentHelperService.offlineActivityAttendeeEnabled(activity);
        this.attendeeButtonLabel = this.attendeeManagmentHelperService.offlineActivityAttendeeButtonLabel(activity);
        this.attending = activity.attendeeStatus === AttendeeStatus.REGISTERED;
        break;
    }
  }

  private initCalendarExport(): void {
    const description = this.htmlToBbCodeParserService.parse(this.event.description);

    switch (this.event.type) {
      case EventType.IN_PERSON:
      case EventType.WEBINAR: {
        const relatedEntity = this.event.relatedEntity as WebinarDTO;
        this.exportCalendarEvent = {
          name: this.event.name,
          timezone: relatedEntity.timezone,
          description,
          date: relatedEntity.date,
          duration: relatedEntity.duration,
          location: relatedEntity.location
        }
        break;
      }
      default:
        this.exportCalendarEvent = {
          name: this.event.name,
          date: moment(this.event.date).utc(true).toISOString(),
          timezone: this.event.timezone,
          description
        };
    }
  }

  public navigate(): void {
    this.router.navigateByUrl(this.relatedEntityLink);
  }

  public updateEventAttendance(): void {
    if (this.isLoading) {
      return;
    }

    let obs: Observable<void>;
    let registered = false;
    const userId = this.profileService.getCurrentProfileValue()?.id;

    this.isLoading = true;
    if (this.event.type === EventType.WEBINAR) {
      const webinar = this.event.relatedEntity as WebinarDTO;
      const notRegisteredStatuses = [UserAttenderStatus.USER_STATUS_NOT_REGISTERED, UserAttenderStatus.USER_STATUS_CANCELED];
      registered = notRegisteredStatuses.indexOf(webinar.attendees.currentUserStatus) === -1;

      obs = registered
        ? this.webinarsService.deleteUserFromWebinar(webinar.id, userId!)
        : this.webinarsService.addUserToWebinar(webinar.id, userId!);
    } else {
      registered = this.event.attendeeStatus === EventAttendeeStatus.ATTENDEE_STATUS_REGISTERED;
        obs = registered
          ? this.eventsApiService.cancelEventAttendace(this.event.id)
          : this.eventsApiService.attendEvent(this.event.id);
    }

    if (!obs) {
      return;
    }

    obs
      .pipe(
        mergeMap(() => this.eventsApiService.getEventById(this.event.id)),
        finalize(() => this.isLoading = false)
      )
      .subscribe(event => {
        this.attendanceChange.emit();

        if (!registered) {
          this.onJoin();
        }

        Object.assign(this.event, { attendeeStatus: event.attendeeStatus, relatedEntity: event.relatedEntity });
        this.initCardButtonsAndLinks();
      });
  }

  public openDetailsDialog(): void {
    this.dialog.open(LearnersEventDialogComponent, {
      data: this.event,
      panelClass: ['mobile-full-width-dialog', 'dialog-no-padding'],
    });
  }

  private setSettings(settings: SettingsModel): void {
    this.cardsSettings = settings.layout.cards;
  }

  public onJoin(): void {
    if (!this.cardsSettings?.event?.addToCalendar?.visible) {
      this.calendarExportService.openCalendarExport(this.exportCalendarEvent);
    }
  }

}
